import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const DEFAULT_EMOJIS = ['🔥', '💪', '👏', '😤', '🙌'];

/**
 * ReactionsRow — shows tap-to-react emoji buttons + aggregated counts for a
 * finished workout. When the current user taps a reaction, an emoji "bursts"
 * upward from the row so squadmates can see the celebration.
 *
 * Props:
 *  - workoutId (required)
 *  - anchorRef (optional) — a ref to the target avatar element so the burst
 *    animates over that avatar instead of the row itself
 *  - variant: 'chip' | 'compact'
 */
export default function ReactionsRow({ workoutId, anchorRef, variant = 'chip', testId }) {
  const [data, setData] = useState(null);
  const [bursts, setBursts] = useState([]); // [{id, emoji, x, y}]
  const rowRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = () => {
      axios.get(`${API_URL}/workouts/${workoutId}/reactions`, { withCredentials: true })
        .then(({ data }) => { if (alive) setData(data); })
        .catch(() => {});
    };
    load();
    return () => { alive = false; };
  }, [workoutId]);

  const spawnBurst = (emoji) => {
    // Anchor burst over the target avatar (if provided) or the tapped chip.
    let host = anchorRef?.current;
    const id = Math.random().toString(36).slice(2);
    if (!host) host = rowRef.current;
    if (!host) return;
    // Store host + emoji; the portal-less floater renders inside host.
    const burst = { id, emoji, host };
    setBursts((prev) => [...prev, burst]);
    setTimeout(() => setBursts((prev) => prev.filter((b) => b.id !== id)), 1200);
  };

  const react = async (emoji) => {
    spawnBurst(emoji);
    try {
      await axios.post(
        `${API_URL}/workouts/${workoutId}/reactions`,
        { emoji },
        { withCredentials: true },
      );
      const { data: fresh } = await axios.get(
        `${API_URL}/workouts/${workoutId}/reactions`,
        { withCredentials: true },
      );
      setData(fresh);
    } catch {}
  };

  const available = data?.available || DEFAULT_EMOJIS;
  const reactions = data?.reactions || [];
  const byEmoji = Object.fromEntries(reactions.map((r) => [r.emoji, r]));

  return (
    <>
      <div
        ref={rowRef}
        className={`relative flex flex-wrap items-center gap-1.5 ${
          variant === 'compact' ? 'text-[10px]' : 'text-[11px]'
        }`}
        data-testid={testId || `reactions-row-${workoutId}`}
      >
        {available.map((e) => {
          const agg = byEmoji[e];
          const count = agg?.count || 0;
          const mine = !!agg?.mine;
          return (
            <button
              key={e}
              onClick={(ev) => { ev.stopPropagation(); react(e); }}
              className={`h-7 px-2 rounded-ios-pill inline-flex items-center gap-1 font-black uppercase tracking-[0.14em] ${
                mine ? 'bg-userRed/12 text-userRed reaction-chip-mine' :
                count > 0 ? 'bg-graphite/8 text-graphite' :
                'bg-graphite/5 text-graphite/60 hover:bg-graphite/10'
              }`}
              data-testid={`reaction-button-${e}`}
              aria-label={`React with ${e}`}
              aria-pressed={mine}
            >
              <span className="text-sm leading-none">{e}</span>
              {count > 0 && <span className="text-[10px] tabular-nums">{count}</span>}
            </button>
          );
        })}
        {/* Local bursts render in place; if anchorRef is set they portal into it. */}
        {bursts.map((b) => (
          <EmojiBurstPortal key={b.id} host={b.host} emoji={b.emoji} />
        ))}
      </div>
    </>
  );
}

function EmojiBurstPortal({ host, emoji }) {
  const [mounted, setMounted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!host) return;
    const el = document.createElement('span');
    el.className = 'emoji-burst';
    el.textContent = emoji;
    // Ensure host can position the burst — add relative if missing.
    const prev = host.style.position;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    // Place at bottom-center-ish
    el.style.left = '50%';
    el.style.top = '0px';
    host.appendChild(el);
    ref.current = el;
    setMounted(true);
    const t = setTimeout(() => {
      if (ref.current && ref.current.parentNode === host) host.removeChild(ref.current);
      if (prev !== undefined) host.style.position = prev;
    }, 1150);
    return () => {
      clearTimeout(t);
      if (ref.current && ref.current.parentNode === host) host.removeChild(ref.current);
    };
  }, [host, emoji]);

  return null;
}
